import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import btnClose from '../../images/common/close.svg';
import Pagination from '../Parts/Pagination';
import List from './List';
import SearchBox from './SearchBox';
import RegistrationDialog from './RegistrationDialog';
import { MODE_DIALOG } from '../../reducers/blackboard';
import {
  createBlackboard,
  deleteBlackboard,
  updateBlackboard,
} from '../../actions/api/apis';
import { PAGE_PATH, REMOTE_COMMAND } from '../../lib/constants';
import ColorSF2 from '../../styles/common/ColorSF2';

const MainBlock = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;
const DialogBox = styled.div`
  max-width: 800px;
  max-height: 640px;
  display: flex;
  flex-direction: column;
  background-color: ${ColorSF2.white};
  @media (max-height: 530px) {
    max-height: 310px;
  }
`;
const HeaderBlock = styled.div`
  padding-left: 18px;
  height: 64px;
  background-color: ${ColorSF2.paleBlue};
  color: ${ColorSF2.black};
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  @media (max-height: 530px) {
    height: 30px;
    font-size: 13px;
  }
`;
const CloseButton = styled.div`
  margin: 20px 18px 20px 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  @media (max-height: 530px) {
    margin: 0px;
  }
`;
const CloseButtonImage = styled.img`
  width: 20px;
  height: 20px;
  @media (max-height: 530px) {
    width: 16px;
    height: 16px;
  }
`;
const BodyBlock = styled.div`
  margin: 8px 8px 10px;
`;
const SearchBoxWrapper = styled.div`
  margin-bottom: 8px;
  width: 100%;
  display: flex;
  justify-content: space-between;
`;
const AddButtonWrapper = styled.div`
  width: 110px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${ColorSF2.white};
  border: 1px solid ${ColorSF2.mediumGray};
  border-left: none;
`;
const AddButton = styled.button`
  width: 66px;
  height: 24px;
  border-radius: 4px;
  border: none;
  background-color: ${ColorSF2.navyBlue};
  color: ${ColorSF2.white};
  font-size: 12px;
  font-weight: bold;
  &:hover {
    opacity: 0.8;
    background-color: ${ColorSF2.navyBlue};
  }
`;

const DeviceBoardListDialog = ({
  dispatch,
  apiToken,
  locale,
  device,
  payload,
  searchOptions,
  dialogTarget,
  onRemoteSupportEnd,
  stringifyOperatorIds,
  favoriteId,
  isGroup,
  resetCheckbox,
}) => {
  const {
    totalNumber = 0,
    totalPages = 0,
    blackboardPresets: list = [],
  } = payload;
  const { page: currentPage = 1 } = searchOptions;
  const { open, mode, blackboard, showError } = dialogTarget;
  const close = () => dispatch({ type: 'CLOSE_BLACKBOARD_LIST_DIALOG' });
  return (
    <MainBlock
      id="closeBlackboardListDialog"
      role="presentation"
      onClick={() => close()}
    >
      <DialogBox role="presentation" onClick={e => e.stopPropagation()}>
        <HeaderBlock>
          <FormattedMessage
            id="blackboardList.title"
            values={{ deviceName: (device && device.deviceNickname) || '' }}
          />
          <CloseButton
            id="closeButton"
            role="presentation"
            className="button"
            onClick={() => close()}
          >
            <CloseButtonImage src={btnClose} alt="close" />
          </CloseButton>
        </HeaderBlock>
        <BodyBlock>
          <SearchBoxWrapper>
            <SearchBox
              searchOptions={searchOptions}
              onUpdate={option =>
                dispatch({ type: 'UPDATE_BLACKBOARD_SEARCH_OPTIONS', option })
              }
            />
            <AddButtonWrapper>
              <AddButton
                id="addBlackboard"
                className="clickableItem"
                role="presentation"
                onClick={() =>
                  dispatch({
                    type: 'OPEN_BLACKBOARD_REGISTRATION_DIALOG',
                    params: { mode: MODE_DIALOG.create },
                  })
                }
              >
                <FormattedMessage id="blackboardList.add" />
              </AddButton>
            </AddButtonWrapper>
          </SearchBoxWrapper>
          <List
            isGroup={isGroup}
            device={device}
            list={list}
            edit={id =>
              dispatch({
                type: 'OPEN_BLACKBOARD_REGISTRATION_DIALOG',
                params: { mode: MODE_DIALOG.edit, id },
              })
            }
            support={blackboardId => {
              resetCheckbox();
              const path = `${PAGE_PATH.DS_REMOTE}/pre/${REMOTE_COMMAND.caller}?operatorIds=${stringifyOperatorIds}&blackboardId=${blackboardId}&favoriteId=${favoriteId}`;
              const link =
                (isGroup && path) || `${path}&deviceId=${device.deviceId}`;
              window.open(link);
            }}
            onRemoteSupportEnd={id => onRemoteSupportEnd(id)}
          />
          <Pagination
            totalNumber={totalNumber}
            totalPages={totalPages}
            currentPage={currentPage}
            onSelect={page =>
              dispatch({ type: 'SELECT_BLACKBOARD_PAGE', page })
            }
          />
          {open && (
            <RegistrationDialog
              locale={locale}
              mode={mode}
              blackboard={blackboard}
              showError={showError}
              close={() =>
                dispatch({ type: 'CLOSE_BLACKBOARD_REGISTRATION_DIALOG' })
              }
              edit={param => dispatch({ type: 'EDIT_BLACKBOARD', param })}
              create={params => dispatch(createBlackboard(apiToken, params))}
              update={params => dispatch(updateBlackboard(apiToken, params))}
              remove={id => dispatch(deleteBlackboard(apiToken, id))}
            />
          )}
        </BodyBlock>
      </DialogBox>
    </MainBlock>
  );
};

DeviceBoardListDialog.propTypes = {
  locale: PropTypes.string.isRequired,
  payload: PropTypes.object.isRequired,
  searchOptions: PropTypes.object.isRequired,
  dialogTarget: PropTypes.object.isRequired,
  device: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  apiToken: PropTypes.string.isRequired,
  onRemoteSupportEnd: PropTypes.func.isRequired,
  stringifyOperatorIds: PropTypes.string.isRequired,
  favoriteId: PropTypes.number,
  isGroup: PropTypes.bool,
  resetCheckbox: PropTypes.func,
};

DeviceBoardListDialog.defaultProps = {
  favoriteId: 0,
  isGroup: false,
  resetCheckbox: () => {},
};

const mapStateToProps = state => ({
  locale: state.main.locale,
  payload: state.blackboard.payload,
  searchOptions: state.blackboard.searchOptions,
  dialogTarget: state.blackboard.dialogTarget,
  device: state.main.supportTargetDevice,
});

export default connect(mapStateToProps)(DeviceBoardListDialog);
